<template>
  <div class="faq-view">
    <v-container>
      <v-row>
        <v-col cols="12" class="text-center">
          <h1 class="text-h3 mb-2">Frequently Asked Questions</h1>
          <p class="subtitle-1 mb-8">
            Find answers to common questions about {{ $config.productName }}
          </p>
        </v-col>
      </v-row>

      <!-- FAQ List -->
      <v-row>
        <v-col cols="12">
          <v-expansion-panels class="faq-panels">
            <v-expansion-panel
              v-for="faq in allFaqs"
              :key="faq.question"
              class="mb-3"
            >
              <v-expansion-panel-header
                class="text-subtitle-1 font-weight-medium"
              >
                {{ faq.question }}
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div v-html="faq.answer" class="faq-answer"></div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>

      <!-- Contact Support -->
      <v-row class="mt-12">
        <v-col cols="12" class="text-center">
          <v-card outlined class="pa-6">
            <v-card-title class="justify-center">
              <v-icon left large color="primary">mdi-help-circle</v-icon>
              Still need help?
            </v-card-title>
            <v-card-text class="text-center">
              Can't find the answer you're looking for? Our support team is here
              to help.
            </v-card-text>
            <v-card-actions class="justify-center">
              <v-btn
                color="primary"
                large
                :href="'mailto:' + $config.SUPPORT_EMAIL"
              >
                <v-icon left>mdi-email</v-icon>
                Contact Support
              </v-btn>
            </v-card-actions>
            <!-- mailto: silently does nothing on machines without a mail
                 client (a churned user reported the button as "broken") -
                 always show the address itself with a copy action. -->
            <v-card-text class="text-center pt-0">
              or email us directly at
              <strong class="support-email" @click="copySupportEmail"
                >{{ $config.SUPPORT_EMAIL }}</strong
              >
              <v-btn
                icon
                small
                title="Copy email address"
                @click="copySupportEmail"
              >
                <v-icon small>{{
                  supportEmailCopied ? "mdi-check" : "mdi-content-copy"
                }}</v-icon>
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import config from "@/config";
import { aiProcessing } from "@shared/utils/productWording";

export default {
  name: "FAQView",
  data() {
    return {
      supportEmailCopied: false
    };
  },
  methods: {
    copySupportEmail() {
      const email = config.SUPPORT_EMAIL;
      const done = () => {
        this.supportEmailCopied = true;
        setTimeout(() => {
          this.supportEmailCopied = false;
        }, 2000);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(email).then(done, done);
      } else {
        // Legacy fallback (non-secure contexts)
        const ta = document.createElement("textarea");
        ta.value = email;
        document.body.appendChild(ta);
        ta.select();
        try {
          document.execCommand("copy");
        } catch (e) {
          /* ignore */
        }
        document.body.removeChild(ta);
        done();
      }
    }
  },
  computed: {
    allFaqs() {
      // App-specific override: the default list below is generic
      // reference-app copy ("upload files, jobs get processed") with
      // literal "[TODO: legal review]" draft markers - wrong, and
      // unfinished, for any app that supplies its own real FAQ content.
      if (config.FAQ_ITEMS) return config.FAQ_ITEMS;
      var grant = this.$store.getters.starterCreditGrant;
      var starterCredits = grant ? Number(grant.amount) : 50;
      return [
        {
          question: `What is ${config.PRODUCT_NAME}?`,
          answer: `
            <p>${config.PRODUCT_NAME} runs ${aiProcessing() ? "AI processing jobs" : "jobs"} on the files you provide, automatically. You upload your input; the system processes it and assembles the result.</p>
            <p>What you get:</p>
            <ul>
              <li>Processing tailored to your specific input, not a generic template</li>
              <li>Consistency carried through the whole job, based on what you provide</li>
              <li>Automatic quality checking, with flawed output regenerated rather than shipped</li>
            </ul>
            <p>The product is under active development and features may change.</p>
          `
        },
        {
          question: "What do I need to upload?",
          answer: `
            <p>What you upload depends on the job type - typically the source file(s) you want processed.</p>
            <p>Some job types accept additional input to guide the result more precisely; without it the system works from what you supplied.</p>
          `
        },
        {
          question: "How long does a job take to run?",
          answer: `
            <p>It varies with the size of the job and how much work it involves, and it depends on how busy the processing queue is. Your plan's concurrent-job limit also affects how many jobs you can have in flight at once.</p>
            <p>We are not quoting a fixed time while the product is in beta - you will see progress as the job runs. <em>[Draft answer - to be firmed up before launch.]</em></p>
          `
        },
        {
          question: "Who owns the results, and can I use them commercially?",
          answer: `
            <p>You keep the rights to what you upload, and you are responsible for holding those rights in the first place. Do not upload material you are not licensed to use.</p>
            <p>Commercial use of the generated result depends on your plan and on the rights covering the material you supplied. <em>[TODO: legal review - this answer needs to be confirmed by counsel before launch.]</em></p>
          `
        },
        {
          question: "What are credits and how do they work?",
          answer: `
            <p>Credits are SaaS usage units for ${config.PRODUCT_NAME} processing time. They are not money, stored value, a wallet balance, or transferable value, and they cannot be withdrawn, transferred, resold, or redeemed for cash.</p>
            <p>Running a job consumes credits based on how much work it takes: a larger job uses more credits than a small one. Regenerating output you are not happy with draws on credits as well.</p>
            <p>Included monthly credits reset each billing cycle and do not roll over. Prices are in USD unless shown otherwise at checkout.</p>
            <p>Free accounts receive ${starterCredits} starter credits once after email verification. They do not refresh. When they are used, subscribe for monthly credits or buy a one-off credit pack.</p>
          `
        },
        {
          question: "When do my credits renew?",
          answer: `
            <ul>
              <li><strong>Free:</strong> starter credits are awarded once and do not renew</li>
              <li><strong>Monthly plans:</strong> credits renew on your subscription anniversary each month</li>
            </ul>
            <p>Included monthly credits <strong>do not roll over</strong>. Your balance is shown in the top navigation bar, and you will be warned when it runs low. A job will not start if you do not have enough credits.</p>
          `
        },
        {
          question: "Can I purchase additional credits?",
          answer: `
            <p>Yes. On the Billing page you can buy a credit pack at any time - enter the number of credits you want and pay through Stripe. Credits are available immediately after payment.</p>
            <p>Subscribers get a better rate per dollar than free accounts, and upgrading your plan raises your monthly allowance.</p>
          `
        },
        {
          question: "What happens if I cancel my subscription?",
          answer: `
            <ul>
              <li><strong>Access:</strong> you keep full access until the end of the billing period you have already paid for</li>
              <li><strong>Credits:</strong> remaining included credits can be used until the end of that period and do not roll over</li>
              <li><strong>Your results:</strong> previously generated results remain available per our data policies</li>
              <li><strong>Reactivation:</strong> you can resubscribe at any time</li>
            </ul>
            <p>After the period ends the account returns to Free plan limits. No new starter grant is issued, and any purchased credits you still hold stay available.</p>
          `
        },
        {
          question: "What formats can I download?",
          answer: `
            <p>Finished results are delivered in standard formats suitable for the usual downstream tools and platforms. <em>[Draft answer - the exact formats offered are still being finalised.]</em></p>
          `
        },
        {
          question: "Is my data secure?",
          answer: `
            <p><strong>Data security:</strong></p>
            <ul>
              <li>Your uploads and generated results travel over encrypted connections (HTTPS)</li>
              <li>Role-based permissions and API key authentication control access</li>
              <li>Your content is only accessible through your own account</li>
            </ul>
            <p><strong>Privacy:</strong></p>
            <ul>
              <li>Your uploads and results are never sold or shared with other users</li>
              ${aiProcessing() ? "<li>To provide AI features, your material is processed by third-party AI infrastructure providers solely to produce the result you asked for</li>" : ""}
            </ul>
            <p>See our Privacy Policy for full details on how data is handled. <em>[TODO: legal review.]</em></p>
          `
        }
      ];
    }
  }
};
</script>

<style scoped>
.support-email {
  cursor: pointer;
  user-select: all;
}

.faq-view {
  background-color: var(--bg-primary);
  min-height: 100vh;
  min-height: 100dvh;
  color: var(--text-primary);
}

/* Title and subtitle styling */
.faq-view :deep(.text-h3) {
  color: var(--text-heading) !important;
}

.faq-view :deep(.subtitle-1) {
  color: var(--text-secondary) !important;
}

/* Expansion panels dark theme */
.faq-panels :deep(.v-expansion-panel) {
  background-color: var(--bg-secondary) !important;
  border: 1px solid var(--border-color) !important;
  border-radius: 8px !important;
  box-shadow: 0 2px 8px var(--shadow-lg) !important;
  margin-bottom: 12px !important;
}

.faq-panels :deep(.v-expansion-panel-header) {
  background-color: var(--bg-secondary) !important;
  color: var(--text-heading) !important;
  padding: 16px 24px !important;
}

.faq-panels :deep(.v-expansion-panel-header:hover) {
  background-color: var(--bg-dark-tertiary) !important;
}

.faq-panels :deep(.v-expansion-panel-content__wrap) {
  padding: 0 24px 16px !important;
  background-color: var(--bg-secondary) !important;
  color: var(--text-primary) !important;
}

/* FAQ answer content */
.faq-answer {
  line-height: 1.6;
  color: var(--text-primary);
}

.faq-answer :deep(p) {
  color: var(--text-primary);
  margin-bottom: 12px;
}

.faq-answer :deep(ul),
.faq-answer :deep(ol) {
  padding-left: 20px;
  margin: 12px 0;
  color: var(--text-primary);
}

.faq-answer :deep(li) {
  margin-bottom: 6px;
  color: var(--text-primary);
}

.faq-answer :deep(code) {
  background-color: var(--bg-dark-tertiary);
  color: var(--color-info-light);
  padding: 2px 6px;
  border-radius: 3px;
  font-family: "Monaco", "Menlo", "Ubuntu Mono", monospace;
  font-size: 0.9em;
}

.faq-answer :deep(strong) {
  color: var(--color-info-light);
  font-weight: 600;
}

/* Support card styling */
.faq-view :deep(.v-card) {
  background-color: var(--bg-secondary) !important;
  border: 1px solid var(--border-color) !important;
  color: var(--text-primary) !important;
}

.faq-view :deep(.v-card-title) {
  color: var(--text-heading) !important;
}

.faq-view :deep(.v-card-text) {
  color: var(--text-secondary) !important;
}
</style>
